//done
//@ts-check
import Alpine from 'alpinejs';
import * as config from '../constants.js';
import { generateContractors, processTeamRegeneration } from "./contractor.js"
import { refreshAlpineInventory } from "./inventoryManager.js";
import { resetRaid } from "./raid.js";
import { getRandomAvatar } from './util.js';
// Import core game state from gameState-core.js (no Alpine dependencies)
import { gameState, saveGameStateCore, loadGameStateCore, isLegacySave } from './gameState-core.js';
import { addNotification } from './notifications.js';
import { debug, isDebug, CH } from './debugLog.js';
export { gameState };

// Cache of last synced character state for change detection
let lastSyncedCharacterState = null;

// Sync character to database if there are changes
export async function syncCharacterToDatabase(character) {
    if (!character) return;

    // Create snapshot of relevant character fields for comparison.
    // Keys are snake_case to match the DB columns the PUT endpoint writes.
    const currentSnapshot = {
        name: character.name,
        faction: character.faction,
        skills: character.skills,
        synergy_unlocks: character.synergyUnlocks,
        skill_points: character.skillPoints,
        max_hp: character.maxHp,
        current_hp: character.currentHp,
        level: character.level,
        xp: character.xp,
        xp_to_next: character.xpToNext,
        avatar: character.avatar,
        avatar_seed: character.avatarSeed,
        avatar_features: character.avatarFeatures,
        title: character.title,
        quote: character.quote,
        inventory: character.inventory,
        artifacts: character.artifacts,
        equipped_artifacts: character.equippedArtifacts,
        equipped_technique_gear: character.equippedTechniqueGear
    };

    // Check if there are actual changes
    if (lastSyncedCharacterState &&
        JSON.stringify(lastSyncedCharacterState) === JSON.stringify(currentSnapshot)) {
        return; // No changes, skip API call
    }

    if (!character.multiplayerId) {
        debug(CH.ui, 'Saved character sync state. Skipping sync call');
        lastSyncedCharacterState = currentSnapshot;
        return; // Only sync characters registered with multiplayer
    }

    try {
        const response = await fetch('/api/characters', {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                // Ownership proof — the server rejects writes to a token-bearing
                // row without a matching token (ServerAuthoritySaveSecurity.md §2).
                ...(character.ownerToken ? { 'Authorization': `Bearer ${character.ownerToken}` } : {})
            },
            body: JSON.stringify({
                id: character.multiplayerId,
                ...currentSnapshot
            })
        });

        if (response.ok) {
            // Update cache only after successful sync
            lastSyncedCharacterState = currentSnapshot;
        } else {
            console.warn('Failed to sync character to database:', await response.text());
        }
    } catch (error) {
        console.warn('Error syncing character to database:', error);
    }
}

// Save/Load System for persistent game progress
export function saveGameState() {
    try {
        const saveData = saveGameStateCore();
        debug(CH.ui, 'Saving game state...', saveData);
        localStorage.setItem('idleExtractionSave', JSON.stringify(saveData));
        //console.log('Game saved successfully');
        //console.log('Saved data:', saveData);

        // Sync character to database (async, non-blocking)
        if (gameState.character) {
            syncCharacterToDatabase(gameState.character);
        }

        return true;
    } catch (error) {
        console.error('Save failed:', error);
        return false;
    }
}

export function loadGameState() {
    try {
        let loaded = false;
        let lastLoaded = Date.now();

        let legacyReset = false;
        const saveData = localStorage.getItem('idleExtractionSave');
        if (saveData) {
            debug(CH.ui, 'Save data found');

            const parsed = JSON.parse(saveData);
            if (parsed.gameState) {
                const knownSkillIds = new Set(
                    Object.values(config.factionSkills || {}).flatMap(f => Object.keys(f.skills || {}))
                );
                if (isLegacySave(parsed, knownSkillIds)) {
                    // Pre-overhaul save: the redesign renamed skills and replaced
                    // ~90% of items, so it cannot be faithfully migrated. Archive
                    // it (recoverable) and start fresh with a one-time notice.
                    console.warn('Legacy (pre-overhaul) save detected - archiving and starting fresh');
                    try { localStorage.setItem('idleExtractionSave_legacyBackup', saveData); } catch (e) { /* quota - proceed */ }
                    localStorage.removeItem('idleExtractionSave');
                    legacyReset = true;
                } else {
                    loadGameStateCore(parsed);
                    loaded = true;
                    lastLoaded = parsed.timestamp;
                }
            }
        }

        if (!loaded) {
            loadGameStateCore({"gameState": gameState});
        }

        if (legacyReset) {
            addNotification('🚀 Idle Raiders got a major overhaul — your old save was incompatible and has been archived. Starting fresh!', 'info');
        }

        // Add avatar to player character if missing
        if (gameState.character && !gameState.character.avatar) {
            gameState.character.avatar = getRandomAvatar(gameState.character.faction);
        }
        // Backfill procedural avatar seed (stable character id) for old saves.
        if (gameState.character && !gameState.character.avatarSeed) {
            gameState.character.avatarSeed = gameState.character.id;
        }

        // Refresh Alpine store if available
        refreshAlpineInventory();
        Alpine.store('contractors').refresh();


        // Validate raidOwnerId - if it references a non-existent character, clear raid data
        if (gameState.currentRaid.raidOwnerId) {
            const raidOwnerExists = gameState.characters.some(c => c.id === gameState.currentRaid.raidOwnerId);
            if (!raidOwnerExists) {
                console.warn('Invalid raidOwnerId found, clearing raid data');
                resetRaid();
                saveGameState();
            }
        }

        // Update contractors if character exists but no contractors generated
        if (gameState.character && gameState.contractors.length === 0) {
            generateContractors();
        }

        // Process offline progress
        const timeDiff = Date.now() - lastLoaded;
        processTeamRegeneration(timeDiff);
       
        saveGameState();

        if (gameState.character && gameState.character.faction)
            document.body.classList.add(gameState.character.faction);
        
        return true;
    } catch (error) {
        console.error('Load failed:', error);
        return false;
    }
}
